import { useState, useEffect } from "react";
import GenericFormTabPo from "../poDetails/tabs/GenericFormTabPo";
import "bootstrap-icons/font/bootstrap-icons.css";
import { gatePassItemFields } from "./gatePassItemFields";
import { toast } from "../../components/AppToast";

const defaultValues = {
  gate_pass_id: "",
  sku_code: "",
  item_name: "",
  quantity: "",
  uom: "EA",
  batch_no: "",
  serial_no: "",
  item_condition: "",
  asset_tag: "",
  remarks: "",
};

const requiredFields = ["sku_code", "item_name", "quantity"];

/** Map Item Master row from `fetchDataAndPatchFields` / `getSkuDetailsById` to gate pass line fields. */
export function mergeGatePassSkuLookup(result, onChange) {
  if (!result) return;
  onChange({ target: { name: "sku_code", value: result.skuId ?? "" } });
  onChange({ target: { name: "item_name", value: result.description ?? "" } });
  onChange({ target: { name: "uom", value: result.uom ?? "" } });
}

export default function GatePassItemDetailsForm({
  initialValues = {},
  onAddToTable,
  onCancel,
  isEditMode,
  readOnly,
}) {
  const [formData, setFormData] = useState({
    ...defaultValues,
    ...initialValues,
  });
  const [formSubmissionAttempted, setFormSubmissionAttempted] = useState(false);

  useEffect(() => {
    setFormData({
      ...defaultValues,
      ...initialValues,
    });
    setFormSubmissionAttempted(false);
  }, [initialValues]);

  const validate = () => {
    const newErrors = {};
    requiredFields.forEach((field) => {
      const v = formData[field];
      if (v === undefined || v === null || String(v).trim() === "") {
        newErrors[field] = "This field is required";
      }
    });
    const qty = Number(formData.quantity);
    if (
      formData.quantity !== "" &&
      formData.quantity != null &&
      (Number.isNaN(qty) || qty <= 0)
    ) {
      newErrors.quantity = "Must be a positive number";
    }
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const numericFields = new Set(["quantity"]);
    if (numericFields.has(name) && value && !/^-?\d*(\.\d*)?$/.test(value)) {
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (readOnly) return;
    setFormSubmissionAttempted(true);
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      toast.error("Fix item line validation errors.");
      const first = Object.keys(validationErrors)[0];
      const el = document.querySelector(`[name="${first}"]`);
      if (el) {
        el.focus();
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }

    const qty = Number(formData.quantity);
    const payload = {
      gate_pass_id: formData.gate_pass_id?.trim() || undefined,
      sku_code: String(formData.sku_code).trim(),
      item_name: String(formData.item_name).trim(),
      quantity: qty,
      uom: String(formData.uom ?? "").trim(),
      batch_no: String(formData.batch_no ?? "").trim(),
      serial_no: String(formData.serial_no ?? "").trim(),
      item_condition: String(formData.item_condition ?? "").trim(),
      asset_tag: String(formData.asset_tag ?? "").trim(),
      remarks: String(formData.remarks ?? "").trim(),
    };

    if (onAddToTable) {
      onAddToTable(payload);
    }

    setFormData({ ...defaultValues });
    setFormSubmissionAttempted(false);
  };

  const handleCancelClick = () => {
    if (onCancel) onCancel();
    setFormData({ ...defaultValues });
    setFormSubmissionAttempted(false);
  };

  return (
    <form onSubmit={handleSubmit} autoComplete="off" className="sto-item-details-form">
      <GenericFormTabPo
        fields={gatePassItemFields}
        formData={formData}
        onChange={handleChange}
        readOnly={readOnly}
        formSubmissionAttempted={formSubmissionAttempted}
        mergeSkuLookupResult={mergeGatePassSkuLookup}
      />
      <div className="text-end mt-4 d-flex justify-content-end gap-2 flex-wrap">
        {!readOnly && isEditMode && (
          <button
            type="button"
            className="btn btn-outline-secondary px-4"
            onClick={handleCancelClick}
          >
            Cancel edit
          </button>
        )}
        {!readOnly && (
          <button type="submit" className="btn btn-primary px-5">
            {isEditMode ? "Update" : "Add"}
          </button>
        )}
      </div>
    </form>
  );
}